import type {ReactNode} from "react";

export type Tab = "home" | "trips" | "world" | "ai" | "nextTrip" | "settings";

export const tabs: Array<{ id: Tab; key: "nav.home" | "nav.trips" | "nav.world" | "nav.ai" | "nextTrip.title" | "nav.menu" }> = [
  { id: "home", key: "nav.home" },
  { id: "trips", key: "nav.trips" },
  { id: "world", key: "nav.world" },
  { id: "ai", key: "nav.ai" },
  { id: "nextTrip", key: "nextTrip.title" },
  { id: "settings", key: "nav.menu" }
];

export function NavigationIcon({ tab }: { tab: Tab }) {
  const pathByTab: Record<Tab, ReactNode> = {
    home: <path d="M4 10.5 12 4l8 6.5V19a1 1 0 0 1-1 1h-5v-5H10v5H5a1 1 0 0 1-1-1z" />,
    trips: <><path d="M3 13.5h6l4.5 6.5h2l-2.5-6.5H21a1.5 1.5 0 0 0 0-3h-8L15.5 4h-2L9 10.5H3a1.5 1.5 0 0 0 0 3Z" /></>,
    world: <><circle cx="12" cy="12" r="8" /><path d="M4.8 9h14.4" /><path d="M4.8 15h14.4" /><path d="M12 4a12.5 12.5 0 0 1 0 16" /><path d="M12 4a12.5 12.5 0 0 0 0 16" /></>,
    ai: <><path d="M12 3.5 14.5 9l5.5 2.5-5.5 2.5L12 19.5 9.5 14 4 11.5 9.5 9 12 3.5Z" /></>,
    nextTrip: <><circle cx="12" cy="12" r="9" /><path d="M8 16l3-7 5-2-2 6-6 3z"/></>,
    settings: <><path d="M5 7h14M5 12h14M5 17h14" /></>
  };
  return (
    <span className={`nav-icon nav-icon--${tab}`} aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none">{pathByTab[tab]}</svg>
    </span>
  );
}


// Canonical per-device navigation preferences, shared with the overflow menu.
export type NavigationLayout = { order: Tab[]; visible: Tab[] };
export const navigationLayoutStorageKey = "mundely.bottom-navigation.v1";
export const navigationLayoutChangedEvent = "mundely:bottom-navigation-changed";
const defaultOrder = tabs.map(item => item.id);

export function readNavigationLayout(): NavigationLayout {
  const defaults = { order: [...defaultOrder], visible: [...defaultOrder] };
  if (typeof localStorage === "undefined") return defaults;
  try {
    const saved = JSON.parse(localStorage.getItem(navigationLayoutStorageKey) || "null") as Partial<NavigationLayout> | null;
    if (!saved) return defaults;
    const order = [...new Set([...(Array.isArray(saved.order) ? saved.order : []), ...defaultOrder])]
      .filter((id): id is Tab => defaultOrder.includes(id as Tab));
    const visible = (Array.isArray(saved.visible) ? saved.visible : defaultOrder)
      .filter((id): id is Tab => order.includes(id as Tab));
    if (!visible.includes("settings")) visible.push("settings");
    return {order, visible:[...new Set(visible)]};
  } catch { return defaults; }
}

export function writeNavigationLayout(next: NavigationLayout): void {
  if (typeof localStorage === "undefined") return;
  const ids = [...defaultOrder];
  const order = [...new Set([...next.order, ...ids])].filter((id): id is Tab => ids.includes(id as Tab));
  const visible = [...new Set(next.visible)].filter((id): id is Tab => order.includes(id as Tab));
  if (!visible.includes("settings")) visible.push("settings");
  localStorage.setItem(navigationLayoutStorageKey, JSON.stringify({order,visible}));
  window.dispatchEvent(new Event(navigationLayoutChangedEvent));
}
